import { supabase } from './auth.service';
import {
  InboxFolder,
  CreateInboxFolderPayload,
  UpdateInboxFolderPayload,
} from '../entities/inbox-folder.entity';
import { mapUser } from '../lib/map-user';

export class InboxFolderService {
  async loadAll(): Promise<InboxFolder[]> {
    const { data, error } = await supabase
      .from('inbox_folders')
      .select('*, createdBy:users!created_by(*)')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data.map(this.mapToEntity);
  }

  async create(payload: CreateInboxFolderPayload): Promise<InboxFolder> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: userRow } = await supabase
      .from('users')
      .select('account_id')
      .eq('id', user.id)
      .single();

    const accountId = userRow?.account_id;
    if (!accountId) throw new Error('User has no account');

    const { data, error } = await supabase
      .from('inbox_folders')
      .insert({
        account_id: accountId,
        name: payload.name,
        description: payload.description,
        visibility: payload.visibility,
        icon: payload.icon,
        color: payload.color,
        filters: payload.filters,
        sort_order: payload.sortOrder,
        created_by: user.id,
      })
      .select('*, createdBy:users!created_by(*)')
      .single();

    if (error) throw error;
    return this.mapToEntity(data);
  }

  async update(id: string, payload: UpdateInboxFolderPayload): Promise<InboxFolder> {
    const { data, error } = await supabase
      .from('inbox_folders')
      .update({
        name: payload.name,
        description: payload.description,
        visibility: payload.visibility,
        icon: payload.icon,
        color: payload.color,
        filters: payload.filters,
        sort_order: payload.sortOrder,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select('*, createdBy:users!created_by(*)')
      .single();

    if (error) throw error;
    return this.mapToEntity(data);
  }

  async delete(id: string): Promise<void> {
    const { error } = await supabase.from('inbox_folders').delete().eq('id', id);

    if (error) throw error;
  }

  private mapToEntity(row: any): InboxFolder {
    return {
      id: row.id,
      accountId: row.account_id,
      name: row.name,
      description: row.description,
      visibility: row.visibility,
      filters: row.filters,
      icon: row.icon,
      color: row.color,
      isActive: row.is_active ?? true,
      sortOrder: row.sort_order ?? 0,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
      createdBy: mapUser(row.createdBy, row.account_id),
    };
  }
}

export const inboxFolderService = new InboxFolderService();
